import React, { useState, useRef, useCallback } from 'react';
import { ArrowUp } from 'lucide-react';
import { ModelSelector } from '@/components/chat/chat-input/ModelSelector';
import { AddContextButton, AddContextButtonHandle } from '@/components/chat/chat-input/AddContextButton';
import { LexicalEditorWrapper, LexicalEditorHandle } from '@/components/chat/chat-input/LexicalEditor';
import type { ContextItem } from '@/components/shared/AddContextMenu';
import type { Plan } from '@/lib/ai/models';

interface StudyInputProps {
  onSendMessage: (text: string, options?: { studyModeTechniqueId?: string | null }) => void;
  onSearchStateChange?: (enabled: boolean) => void;
  onContentReferencesChange?: (contentIds: string[]) => void;
  onStudyModeTechniqueChange?: (techniqueId: string | null) => void;
  placeholder?: string;
  maxWidth?: 'md' | 'lg' | 'xl' | '2xl' | '3xl';
  userPlan?: Plan;
  onUpgradeRequest?: () => void;
}

export const StudyInput: React.FC<StudyInputProps> = ({ 
  onSendMessage,
  onSearchStateChange,
  onContentReferencesChange,
  onStudyModeTechniqueChange,
  placeholder = 'Study anything with Shattara AI',
  maxWidth = '2xl',
  userPlan,
  onUpgradeRequest,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [selectedModel, setSelectedModel] = useState('auto');
  const [sendTrigger, setSendTrigger] = useState(0);
  const [clearTrigger, setClearTrigger] = useState(0);
  const [hasContent, setHasContent] = useState(false);
  const [activeTechniqueId, setActiveTechniqueId] = useState<string | null>(null);
  const contextButtonRef = useRef<AddContextButtonHandle>(null);
  const editorRef = useRef<LexicalEditorHandle>(null);

  const showControls = isFocused;

  const handleSend = useCallback((text: string) => {
    if (!text.trim()) return;
    onSendMessage(text, { studyModeTechniqueId: activeTechniqueId });
    setClearTrigger(prev => prev + 1);
    setHasContent(false);
  }, [onSendMessage, activeTechniqueId]);

  const handleSendButtonClick = () => {
    setSendTrigger(prev => prev + 1);
  };

  const handleOpenContextMenu = useCallback(() => {
    contextButtonRef.current?.openMenu();
  }, []);

  const handleMentionInsert = useCallback((item: ContextItem) => {
    editorRef.current?.insertMention(item);
  }, []);

  const handleSelectedItemsChange = useCallback((items: ContextItem[]) => {
    const hasSearch = items.some((item) => item.id === 'search');
    onSearchStateChange?.(hasSearch);

    const contentIds = items
      .filter(item => item.group === 'Recents')
      .map(item => item.id);
    onContentReferencesChange?.(contentIds);

    // Detect active study-mode technique from Chat Instructions selection
    const instruction = items.find((item) => item.group === 'Chat Instructions' && item.id.startsWith('study-mode:'));
    const techId = instruction ? instruction.id.replace('study-mode:', '') : null;
    setActiveTechniqueId(techId);
    onStudyModeTechniqueChange?.(techId);
  }, [onSearchStateChange, onContentReferencesChange, onStudyModeTechniqueChange]);

  const handleBlur = (e: React.FocusEvent) => {
    const currentTarget = e.currentTarget;
    setTimeout(() => {
      if (!currentTarget.contains(document.activeElement)) {
        setIsFocused(false);
      }
    }, 0);
  };

  const maxWidthClasses = {
    'md': 'md:max-w-md',
    'lg': 'md:max-w-lg',
    'xl': 'md:max-w-xl',
    '2xl': 'xl:max-w-2xl md:max-w-lg',
    '3xl': 'xl:max-w-3xl md:max-w-xl'
  };

  return (
    <div className={`w-full ${maxWidthClasses[maxWidth]}`}>
      <div 
        className="flex w-full flex-col transition-all py-1.5 duration-150 justify-center pr-2 pl-3.5 border rounded-3xl shadow-sm border-border bg-card relative"
        onBlur={handleBlur}
        onFocus={() => setIsFocused(true)}
      >
        <div className="flex flex-col w-full relative transition-all duration-500 ease-in">
          <div className="flex w-full items-center min-h-[38px]">
            <div className="relative w-full text-sm focus-within:outline-none focus-within:ring-none pr-12">
              <LexicalEditorWrapper
                editorRef={editorRef}
                onSend={handleSend}
                disabled={false}
                clearTrigger={clearTrigger}
                sendTrigger={sendTrigger}
                placeholder={placeholder}
                onOpenContextMenu={handleOpenContextMenu}
                onTextChange={(text) => setHasContent(!!text.trim())}
              />
            </div>
          </div>

          <div 
            className={`transition-all relative overflow-visible ${
              showControls 
                ? 'max-h-[420px] opacity-100 mb-0' 
                : 'max-h-0 opacity-0 mb-0 overflow-hidden'
            }`}
          >
            <div className="flex flex-row justify-between items-center gap-1 pt-1 pb-1">
              <div className="flex flex-row items-center gap-1 flex-wrap">
                <ModelSelector 
                  selectedModel={selectedModel}
                  onModelChange={setSelectedModel}
                  userPlan={userPlan}
                  onUpgradeRequest={onUpgradeRequest}
                />
                <AddContextButton 
                  ref={contextButtonRef}
                  onMentionInsert={handleMentionInsert}
                  onSelectedItemsChange={handleSelectedItemsChange}
                />
              </div>
            </div>
          </div>

          <div className="absolute right-0 top-0">
            <button
              type="button"
              onClick={handleSendButtonClick}
              disabled={!hasContent}
              className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none rounded-full h-[38px] w-[38px] bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:pointer-events-none"
            >
              <ArrowUp className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
